import apiClient from '../apiClient';

// Policy Management APIs
export const policyService = {
  // Get all policies
  getPolicies: async (params = {}) => {
    try {
      const response = await apiClient.get('/policies/policies', {
        params
      });
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Get single policy by ID
  getPolicyById: async (policyId) => {
    try {
      const response = await apiClient.get(`/policies/policies/${policyId}`);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Create new policy
  createPolicy: async (policyData) => {
    try {
      // Check if policyData is FormData (for file upload)
      const isFormData = policyData instanceof FormData;
      const config = isFormData ? {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      } : {};

      const response = await apiClient.post('/policies/policies', policyData, config);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Update policy
  updatePolicy: async (policyId, policyData) => {
    try {
      // Check if policyData is FormData (for file upload)
      const isFormData = policyData instanceof FormData;
      const config = isFormData ? {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      } : {};

      const response = await apiClient.put(`/policies/policies/${policyId}`, policyData, config);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Delete policy
  deletePolicy: async (policyId) => {
    try {
      const response = await apiClient.delete(`/policies/policies/${policyId}`);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Download policy
  downloadPolicy: async (policyId) => {
    try {
      const response = await apiClient.get(`/policies/policies/${policyId}/download`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  // Download generic url
  downloadUrl: async (url, filename) => {
    try {
      const response = await apiClient.get(`/policies/download-url`, {
        params: { url, filename },
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  // Get current user's policy stats (total active, acknowledged, pending)
  getMyPolicyStats: async () => {
    try {
      const response = await apiClient.get('/policies/my-stats');
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Get policy management metrics
  getManagementMetrics: async () => {
    try {
      const response = await apiClient.get('/policies/management-metrics');
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Upload to Cloudinary
  uploadCloudinary: async (file, folder = 'hrms-policies') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await apiClient.post('/upload/cloudinary', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000 // 5 minutes timeout for large file uploads
      });

      if (response.data && response.data.success) {
        return response.data.data;
      } else if (response.success) {
        return response.data;
      }
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Delete from Cloudinary
  deleteCloudinary: async (publicId, resourceType = 'raw') => {
    try {
      const response = await apiClient.delete('/upload/cloudinary', {
        params: {
          public_id: publicId,
          resource_type: resourceType
        }
      });
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Get policy categories for dropdown
  getPolicyCategories: async () => {
    try {
      const response = await apiClient.get('/config/policy-categories/dropdown');
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  }
};

// Acknowledgment Management APIs
export const acknowledgmentService = {
  // Get acknowledgments for a policy
  getPolicyAcknowledgments: async (policyId, params = {}) => {
    try {
      const response = await apiClient.get(`/policies/acknowledgments`, {
        params: { policyId, ...params }
      });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Get all acknowledgments
  getAllAcknowledgments: async (params = {}) => {
    try {
      const response = await apiClient.get('/policies/acknowledgments', { params });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Get acknowledgments (alias for getAllAcknowledgments)
  getAcknowledgments: async (params = {}) => {
    try {
      

      const response = await apiClient.get('/policies/acknowledgments', {
        params
      });

      
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Create acknowledgment
  createAcknowledgment: async (acknowledgmentData) => {
    try {
      const response = await apiClient.post('/policies/acknowledgments', acknowledgmentData);
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  // Update acknowledgment
  updateAcknowledgment: async (acknowledgmentId, acknowledgmentData) => {
    try {
      const response = await apiClient.put(`/policies/acknowledgments/${acknowledgmentId}`, acknowledgmentData);
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  // Delete acknowledgment
  deleteAcknowledgment: async (acknowledgmentId) => {
    try {
      const response = await apiClient.delete(`/policies/acknowledgments/${acknowledgmentId}`);
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  // Get acknowledgment statistics
  getAcknowledgmentStats: async (params = {}) => {
    try {
      // Cache-busting via query params (avoids CORS preflight from custom headers)
      const cacheBustingParams = {
        ...params,
        _t: Date.now(),
        _r: Math.random().toString(36).substring(7),
      };

      const response = await apiClient.get('/policies/acknowledgments/stats', {
        params: cacheBustingParams,
      });

      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Send bulk reminder
  sendBulkReminder: async (reminderData) => {
    try {
      const response = await apiClient.post('/policies/acknowledgments/bulk-reminder', reminderData);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Send single acknowledgment reminder
  sendReminder: async (acknowledgmentId) => {
    try {
      const response = await apiClient.post(`/policies/acknowledgments/remind/${acknowledgmentId}`);
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Export acknowledgments
  exportAcknowledgments: async (params = {}) => {
    try {
      const response = await apiClient.get('/policies/acknowledgments/export', {
        params,
        responseType: 'blob'
      });
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  },

  // Fetch acknowledgments data for frontend-side export
  fetchExportData: async (params = {}) => {
    try {
      const response = await apiClient.get('/policies/acknowledgments/export', {
        params: { ...params, format: 'json' }
      });
      return response.data || response;
    } catch (error) {
      
      throw error;
    }
  }
};

// Audit Logs APIs
export const auditLogService = {
  // Get audit logs
  getAuditLogs: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/audit-logs', { params });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Get audit log by ID
  getAuditLogById: async (logId) => {
    try {
      const response = await apiClient.get(`/api/audit-logs/${logId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }
};

// Compliance Reports APIs
export const complianceReportService = {
  // Get compliance overview
  getComplianceOverview: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/compliance/reports/overview', { params });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Get policy compliance status
  getPolicyComplianceStatus: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/compliance/reports/policy-status', { params });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Get acknowledgment reports
  getAcknowledgmentReports: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/compliance/reports/acknowledgments', { params });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  },

  // Export compliance data
  exportComplianceData: async (format = 'pdf', params = {}) => {
    try {
      const response = await apiClient.get(`/api/compliance/reports/export/${format}`, {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }
};

export default {
  policyService,
  acknowledgmentService,
  auditLogService,
  complianceReportService
};
